import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button
} from '@mui/material';
import { api } from 'services/api';
import { formatNumber } from '../../utils/formatters';

interface AssetAmount {
  assetName: string;
  assetCode: string;
  amount: string;
  usdValue: number;
}

interface AssetBalanceAnalytics {
  assetCode: string;
  assetName: string;
  totalBalance: string;
  brokerCommission: string;
  exchangeRemainder: string;
  netBalance: string;
}

interface FinancialAnalytics {
  totalDepositsUsd: number;
  totalBalanceUsd: number;
  deposits: AssetAmount[];
  balances: AssetAmount[];
  updatedAt: string;
  balanceAnalytics: {
    assetBalances: AssetBalanceAnalytics[];
    totalBalances: string;
    operationProfits: string;
  };
  filter?: {
    date: string | null;
    assetCode: string | null;
    description: string;
  };
}

interface ApiResponse {
  success: boolean;
  data: FinancialAnalytics;
}

const AnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<FinancialAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterDate, setFilterDate] = useState<string>('');
  const [filterAsset, setFilterAsset] = useState<string>('');
  const [filterInfo, setFilterInfo] = useState<string>('All time data');

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filterDate) {
        params.append('date', filterDate);
      }
      if (filterAsset) {
        params.append('assetCode', filterAsset);
      }
      
      const response = await api.get<ApiResponse>(
        `/api/v1/admin-panel/analytics/financial?${params.toString()}`
      );
      
      setAnalytics(response.data.data);
      setFilterInfo(response.data.data.filter?.description || 'All time data');
      setError(null);
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Keep the 5-minute refresh interval
    const interval = setInterval(fetchAnalytics, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFilter = () => {
    fetchAnalytics();
  };

  const handleClearFilter = () => {
    setFilterDate('');
    setFilterAsset('');
    setTimeout(() => fetchAnalytics(), 100);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Финансовая аналитика
      </Typography>

      {/* Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Фильтры
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Дата"
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                helperText="Показать данные за конкретный день"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Код актива"
                value={filterAsset}
                onChange={(e) => setFilterAsset(e.target.value.toUpperCase())}
                placeholder="BTC, ETH, USDT..."
                fullWidth
                size="small"
                helperText="Фильтр по активу"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="contained" 
                onClick={handleFilter}
                fullWidth
              >
                Применить фильтр
              </Button>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button 
                variant="outlined" 
                onClick={handleClearFilter}
                fullWidth
              >
                Показать все
              </Button>
            </Grid>
          </Grid>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            <strong>Данные:</strong> {filterInfo}
          </Typography>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Общая сумма депозитов
              </Typography>
              <Typography variant="h4" color="primary">
                ${formatNumber(analytics?.totalDepositsUsd || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Текущий баланс системы
              </Typography>
              <Typography variant="h4" color="primary">
                ${formatNumber(analytics?.totalBalanceUsd || 0)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                (не фильтруется)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Прибыль с операций
              </Typography>
              <Typography variant="h4" color="primary">
                ${formatNumber(Number(analytics?.balanceAnalytics?.operationProfits || 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tables */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Депозиты по активам
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Актив</TableCell>
                  <TableCell align="right">Количество</TableCell>
                  <TableCell align="right">USD</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics?.deposits?.map((deposit) => (
                  <TableRow key={deposit.assetCode}>
                    <TableCell>
                      {deposit.assetName} ({deposit.assetCode})
                    </TableCell>
                    <TableCell align="right">{formatNumber(Number(deposit.amount))}</TableCell>
                    <TableCell align="right">${formatNumber(deposit.usdValue)}</TableCell>
                  </TableRow>
                ))}
                {analytics?.deposits?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="textSecondary">
                        Нет данных для отображения
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Текущие балансы по активам
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Актив</TableCell>
                  <TableCell align="right">Количество</TableCell>
                  <TableCell align="right">USD</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analytics?.balances?.map((balance) => (
                  <TableRow key={balance.assetCode}>
                    <TableCell>
                      {balance.assetName} ({balance.assetCode})
                    </TableCell>
                    <TableCell align="right">{formatNumber(Number(balance.amount))}</TableCell>
                    <TableCell align="right">${formatNumber(balance.usdValue)}</TableCell>
                  </TableRow>
                ))}
                {analytics?.balances?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="textSecondary">
                        Нет данных для отображения
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Balance Analytics Table - KEPT ALL EXISTING LOGIC */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Остатки на балансах по активам
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Актив</TableCell>
              <TableCell align="right">Общий баланс</TableCell>
              <TableCell align="right">Комиссия брокера</TableCell>
              <TableCell align="right">Остаток обмена</TableCell>
              <TableCell align="right">Чистый баланс</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analytics?.balanceAnalytics?.assetBalances?.map((assetBalance) => (
              <TableRow key={assetBalance.assetCode}>
                <TableCell>
                  {assetBalance.assetName} ({assetBalance.assetCode})
                </TableCell>
                <TableCell align="right">{formatNumber(Number(assetBalance.totalBalance))}</TableCell>
                <TableCell align="right">{formatNumber(Number(assetBalance.brokerCommission))}</TableCell>
                <TableCell align="right">{formatNumber(Number(assetBalance.exchangeRemainder))}</TableCell>
                <TableCell align="right">{formatNumber(Number(assetBalance.netBalance))}</TableCell>
              </TableRow>
            ))}
            {analytics?.balanceAnalytics?.assetBalances?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="textSecondary">
                    Нет данных для отображения
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2} textAlign="right">
        <Typography variant="caption" color="textSecondary">
          Последнее обновление: {new Date(analytics?.updatedAt || '').toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default AnalyticsPage;